import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import classnames from 'classnames'
import Typography from '../Typography'
import IconButton from '../IconButton'
import type { StyleClasses } from '../../styles'
import AlertIcon from './AlertIcon'

export interface AlertProps {
  children?: React.ReactNode
  className?: string
  classes?: StyleClasses<typeof useStyles>
  content?: React.ReactNode
  title?: string
  tooltip?: string
  noMargin?: boolean
  icon?: React.ReactNode
  hideIcon?: boolean
  severity?: 'warning' | 'error' | 'info' | 'success' | 'default'
  variant?: 'default' | 'outlined' | 'filled' | 'text'
  centerIcon?: boolean
  onClose?: () => void
}

function getSeverityColor(severity: AlertProps['severity'], theme: Theme) {
  switch (severity) {
    case 'warning':
      return theme.palette.warning
    case 'error':
      return theme.palette.error
    case 'success':
      return theme.palette.success
    case 'info':
      return theme.palette.info
    default:
      return {
        light: theme.paintscout.palette.brand.Neutral.P94,
        main: theme.paintscout.palette.brand.Neutral.P70,
        dark: theme.paintscout.palette.brand.Neutral.P30,
        contrastText: '#fff'
      }
  }
}

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: ({ severity = 'default', variant = 'default', noMargin = false }: AlertProps) => {
        const severityColor = getSeverityColor(severity, theme)
        let background = 'transparent'
        if (variant === 'default') {
          background = severityColor.light
        } else if (variant === 'filled') {
          background = severityColor.main
        }

        const borderColor = variant === 'default' ? severityColor.light : severityColor.main

        const color = variant === 'filled' ? severityColor.contrastText : severityColor.dark

        return {
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: theme.borderRadius.md,
          padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor,
          background,
          color,
          position: 'relative',
          marginBottom: noMargin ? 0 : theme.spacing(2)
        }
      },
      contentWrapper: {
        fontSize: theme.typography.body1.fontSize,
        width: '100%'
      },
      iconWrapper: ({ severity = 'default', variant = 'default' }: AlertProps) => {
        const severityColor = getSeverityColor(severity, theme)
        return {
          display: 'flex',
          alignItems: 'center',
          color: variant === 'filled' ? '#fff' : severityColor.main,
          marginTop: 2,
          marginBottom: '0px !important',
          '& svg': {
            width: 20,
            height: 20
          },
          marginRight: theme.spacing(0.5)
        }
      },
      closeWrapper: {
        position: 'absolute',
        top: 6,
        right: 0
      },
      title: {
        marginBottom: theme.spacing(0.5),
        color: 'inherit'
      },
      content: {}
    }
  },
  {
    name: 'Alert'
  }
)

export default function Alert({
  severity = 'default',
  variant = 'default',
  hideIcon,
  children,
  content,
  title,
  className,
  onClose,
  icon: iconProp,
  ...other
}: AlertProps) {
  const classes = useStyles({ severity, variant, ...other })
  const icon = hideIcon ? null : typeof iconProp === 'undefined' ? <AlertIcon severity={severity} /> : iconProp

  return (
    <div className={classnames(classes.root, className)}>
      {icon && <div className={classes.iconWrapper}>{icon}</div>}
      <div className={classes.contentWrapper}>
        {title && (
          <Typography classes={{ root: classes.title }} variant={'h4'} component="p">
            {title}
          </Typography>
        )}
        {children ? <>{children}</> : <AlertContent />}
      </div>
      {onClose && (
        <div className={classes.closeWrapper}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </div>
  )

  function AlertContent() {
    return <>{content && <Typography classes={{ root: classes.content }}>{content}</Typography>}</>
  }
}
